import axios from 'axios';
import { backendUrl } from '../backendUrl';

// Create a dedicated axios instance for admin API calls
const adminApi = axios.create({
  baseURL: `${backendUrl}/api/v1/configurator/admin`,
});

// Attach JWT token to every request
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 = nieważny lub wygasły token -> wylogowanie.
// 403 = brak uprawnienia do operacji - sesja zostaje, widok pokazuje błąd.
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminPermissions');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export type LoginResult = 'ok' | 'invalid' | 'rate_limited' | 'error';

export async function adminLogin(username: string, password: string): Promise<LoginResult> {
  try {
    const response = await axios.post(`${backendUrl}/api/login`, { username, password });
    const { token, permissions } = response.data;
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminPermissions', JSON.stringify(permissions || []));
    return 'ok';
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 429) return 'rate_limited';
    if (status === 401) return 'invalid';
    return 'error';
  }
}

export function getAdminPermissions(): string[] {
  try {
    const perms = localStorage.getItem('adminPermissions');
    return perms ? JSON.parse(perms) : [];
  } catch (e) {
    return [];
  }
}

// Login zalogowanego użytkownika z tokenu JWT (wcześniej w panelu zawsze widniało "admin")
export function getAdminUsername(): string {
  const token = localStorage.getItem('adminToken');
  if (!token) return '';
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes));
    return typeof payload.username === 'string' ? payload.username : '';
  } catch (e) {
    return '';
  }
}

export function adminLogout(): void {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminPermissions');
}

export function isAdminLoggedIn(): boolean {
  return !!localStorage.getItem('adminToken');
}

export default adminApi;
